import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, PauseCircle } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Crumbs } from "../components/Shared";
import { useToast } from "../hooks/use-toast";
import { SITE } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors";

export default function MembershipHold() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", start: "", duration: "1 month", reason: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: "",
        message: `[Membership Hold Request] Hold start date: ${form.start}. Duration: ${form.duration}. Reason: ${form.reason || "not given"}.`,
      });
      setDone(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: `Please try again or email ${SITE.email}.` });
    } finally {
      setSending(false);
    }
  };

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Membership Hold", path: "/membership-hold-request" },
  ];

  return (
    <>
      <Seo
        title="Membership Hold Request | Boston Iron Grit"
        description="Request a temporary hold on your Boston Iron Grit membership. Tell us your hold start date and duration and we'll confirm by email."
        path="/membership-hold-request"
        noindex
      />
      <section className="bg-[#0A0A0B] py-14 lg:py-20 min-h-[70vh]">
        <div className="max-w-xl mx-auto px-6">
          <Reveal>
            <Crumbs items={crumbs} />
            <PauseCircle size={30} className="mt-8 text-[#9D0423]" />
            <h1 className="mt-4 font-heading font-extrabold uppercase text-4xl text-white tracking-tight">Membership hold request</h1>
            <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed">
              Need to pause? Submit the form and we'll confirm your hold by email. Holds take effect per your membership agreement.
            </p>
            {done ? (
              <div className="mt-10 p-7 bg-[#121214] border border-[#9D0423]/30" data-testid="hold-success">
                <CheckCircle2 size={28} className="text-[#9D0423]" />
                <p className="mt-4 text-neutral-200 text-[15px]">
                  Request received. We'll confirm your hold at <span className="text-white font-semibold">{form.email}</span> within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-4" data-testid="hold-form">
                <input required value={form.name} onChange={set("name")} placeholder="Full name *" aria-label="Full name" data-testid="hold-name" className={inputCls} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" aria-label="Email" data-testid="hold-email" className={inputCls} />
                <div>
                  <label htmlFor="hold-start" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Hold start date *</label>
                  <input id="hold-start" required type="date" value={form.start} onChange={set("start")} data-testid="hold-start" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="hold-duration" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Duration *</label>
                  <select id="hold-duration" value={form.duration} onChange={set("duration")} data-testid="hold-duration" className={inputCls}>
                    {["1 month", "2 months", "3 months"].map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <textarea rows={3} value={form.reason} onChange={set("reason")} placeholder="Reason (optional)" aria-label="Reason" data-testid="hold-reason" className={inputCls} />
                <button type="submit" disabled={sending} data-testid="hold-submit" className="w-full bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-6 py-4 transition-colors">
                  {sending ? "Sending…" : "Submit Hold Request"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
